import "./index.scss";

import { Button } from "@shared/components";
import { changeListeners, formSubmitHandler, schemaValidators, useAppForm } from "@shared/form";
import { Meeting, User } from "@shared/models";
import React, { useMemo } from "react";
import { CreateUserMeetingDto } from "@containers/User/interfaces/UserForm.interface";

import { getInitValues, prepareSubmitValue, validationSchema, MEETING_STATUS_OPTIONS } from "./formHelpers";

const MAX_TEXTAREA_LENGTH = 200;
const FORM_ID = "create-edit-meeting-form";

interface CreateEditMeetingFormProps {
  meeting: Meeting | null;
  currentUser: User | null;
  onChangeForm: () => void;
  onCloseForm: () => void;
  onDelete: () => void;
  onSubmitForm: (payload: Omit<CreateUserMeetingDto, "contractor_id">) => void;
}

const CreateEditMeetingFormFields: React.FunctionComponent<CreateEditMeetingFormProps> = (props) => {
  const { meeting, onChangeForm, onCloseForm, onDelete, onSubmitForm, currentUser } = props;
  const formValues = useMemo(() => getInitValues(meeting), [meeting]);
  const ownMeeting = useMemo(() => !meeting || currentUser?.id === meeting.manager?.id, [meeting, currentUser]);

  const isDisabledFields = Boolean(meeting?.id);

  const form = useAppForm({
    defaultValues: formValues,
    validators: schemaValidators(validationSchema),
    listeners: changeListeners(onChangeForm),
    onSubmit: ({ value }) => {
      onSubmitForm(prepareSubmitValue(value));
    },
  });

  return (
    <div className="user-meeting-form">
      <form id={FORM_ID} onSubmit={formSubmitHandler(form)}>
        <div className="user-meeting-form-content">
          <div className="meeting-title-wrapper">
            <form.AppField name="title">
              {(field) => (
                <field.Input className="meeting-title-input" type="text" labelTitle="Meeting" placeholder="Meeting" />
              )}
            </form.AppField>
          </div>
          <div className="meeting-date-wrapper">
            <form.AppField name="date">
              {(field) => (
                <field.Input
                  className="meeting-date-input"
                  type="date"
                  labelTitle="Date"
                  placeholder="Date"
                  disabled={isDisabledFields}
                />
              )}
            </form.AppField>
          </div>
          {ownMeeting && (
            <div className="meeting-next-date-wrapper">
              <label className="add-to-calendar-label">
                {/* The checkbox stores "1" / "0" and no adapter fits it, so it is wired to the field here. */}
                <form.Field name="add_to_google_calendar">
                  {(field) => (
                    <input
                      disabled={Boolean(Number(meeting?.add_to_google_calendar))}
                      name="google-calendar-checkbox"
                      type="checkbox"
                      className="google-calendar-checkbox"
                      value={"add"}
                      checked={Boolean(Number(field.state.value))}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.checked ? "1" : "0")}
                    />
                  )}
                </form.Field>
                <div className="google-calendar-label-text">Add to Calendar</div>
              </label>
            </div>
          )}
          <div className="meeting-comment-wrapper">
            <form.AppField name="comment">
              {(field) => (
                <>
                  <div className="comment-textarea-length">
                    {field.state.value.length}/{MAX_TEXTAREA_LENGTH}
                  </div>
                  <field.Textarea
                    className="meeting-comment-input"
                    maxLength={MAX_TEXTAREA_LENGTH}
                    labelTitle="Comment"
                    placeholder="Comment"
                  />
                </>
              )}
            </form.AppField>
          </div>
          <div className="procedure-process-input-wrapper">
            <div className="procedure-process-input">
              <form.AppField name="status">
                {(field) => (
                  <field.Select
                    searchable={false}
                    cleanable={false}
                    data={MEETING_STATUS_OPTIONS}
                    labelTitle="Status"
                    placeholder="Status"
                  />
                )}
              </form.AppField>
            </div>
          </div>
        </div>
      </form>

      <div className="form-footer">
        <div className="form-footer-left">
          {meeting ? (
            <Button type="button" className="clear padding" onClick={onDelete}>
              Delete
            </Button>
          ) : null}
        </div>
        <div className="form-footer-right">
          <Button type="button" className="close-btn margin-right" onClick={() => onCloseForm()}>
            Cancel
          </Button>
          <Button type="submit" form={FORM_ID} className="accent padding">
            {meeting ? "Save" : "Create Meeting"}
          </Button>
        </div>
      </div>
    </div>
  );
};

/** Keyed on the meeting's initial values: a meeting that arrives or changes while the form is open mounts a fresh form. */
const CreateEditMeetingForm: React.FunctionComponent<CreateEditMeetingFormProps> = (props) => (
  <CreateEditMeetingFormFields key={JSON.stringify(getInitValues(props.meeting))} {...props} />
);

export default CreateEditMeetingForm;
